import React from "react";
import { motion } from "framer-motion";
import { FaInstagram, FaYoutube, FaFacebookF, FaWhatsapp, FaArrowRight } from "react-icons/fa";
import { FiArrowUpRight, FiCode } from "react-icons/fi";
import { HiSparkles } from "react-icons/hi2";
import { Link } from "react-router-dom";
import packageJson from "../../package.json";

const navigationLinks = [
  { name: "Home",         path: "/" },
  { name: "About Us",     path: "/about-us" },
  { name: "Benefits",     path: "/benefits" },
  { name: "Glimpses",  path: "/gallery" },
  { name: "Contact",      path: "/contact" },
];

const quickLinks = [
  { name: "Courses",      path: "/courses" },
  { name: "Live Classes", path: "/live-classes" },
  { name: "Trainers",     path: "/trainers" },
  { name: "Community",    path: "/community" },
  { name: "Blog",         path: "/blog" },
  { name: "Pricing",      path: "/pricing" },
];

const legalLinks = [
  { name: "Privacy Policy",     path: "/privacy-policy" },
  { name: "Terms & Conditions", path: "/terms-conditions" },
  { name: "Refund Policy",      path: "/refund-policy" },
  { name: "Cookie Policy",      path: "/cookie-policy" },
];

const socials = [
  { icon: <FaInstagram />, label: "Instagram", url: "https://www.instagram.com/divyyog.foundation" },
  { icon: <FaYoutube />,   label: "YouTube",   url: "https://www.youtube.com/@divyogafoundation1169" },
  { icon: <FaFacebookF />, label: "Facebook",  url: "https://www.facebook.com/people/Divyyogfoundation-Gondia/61558710747446/" },
  { icon: <FaWhatsapp />,  label: "WhatsApp",  url: "https://wa.me" },
];

const Footer = () => {
  return (
    <footer className="relative overflow-hidden py-12">

      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#120700] via-[#1B0D00] to-[#2B1400]" />
      <motion.div
        animate={{ scale:[1,1.08,1], opacity:[0.08,0.18,0.08] }}
        transition={{ repeat:Infinity, duration:10 }}
        className="absolute top-[-250px] right-[-180px] w-[700px] h-[700px] rounded-full bg-[#F29A0C]/20 blur-[180px]"
      />
      <div className="absolute bottom-[-200px] left-[-180px] w-[500px] h-[500px] rounded-full bg-[#F29A0C]/10 blur-[140px]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.08),transparent_30%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.05),transparent_30%)]" />
      <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay bg-[url('https://www.transparenttextures.com/patterns/asfalt-light.png')]" />

      {/* Content */}
      <div className="relative z-20 max-w-[1650px] mx-auto px-6 lg:px-20">

        {/* Top */}
        <div className="grid lg:grid-cols-[1.1fr_1.1fr] gap-16 pb-12">

          {/* Left */}
          <div>
            <motion.div whileHover={{ scale:1.02 }} className="flex items-center gap-5">
              <div className="relative overflow-hidden w-20 h-20 rounded-[28px] border border-white/10 bg-white/10 backdrop-blur-2xl shadow-[0_20px_60px_rgba(0,0,0,0.20)]">
                <img src="/assets/logo.jpeg" alt="Divyyog Foundation" className="w-full h-full object-cover" />
              </div>
              <div>
                <h2 className="text-white text-[34px] leading-none tracking-[1px] font-semibold" style={{ fontFamily:"Poppins" }}>
                  Divyyog
                </h2>
                <p className="text-white/55 text-sm tracking-[4px] uppercase mt-2">Foundation</p>
              </div>
            </motion.div>

            <h3 className="text-white text-[38px] md:text-[34px] leading-[1.0] tracking-[1px] font-semibold mt-12 max-w-[700px]" style={{ fontFamily:"Poppins" }}>
              Rooted in Tradition.<br />
              Connected to<span className="text-[#F29A0C]"> the World.</span>
            </h3>

            <p className="text-white/65 text-[17px] leading-[36px] mt-8 max-w-[620px]">
              Divyyog Foundation brings authentic yoga, mindfulness, and wellness experiences to
              students globally through immersive digital learning and transformative practice.
            </p>

            <div className="flex flex-wrap gap-5 py-4">
              {socials.map((item, i) => (
                <motion.a key={i} href={item.url} target="_blank" rel="noopener noreferrer" aria-label={item.label}
                  whileHover={{ y:-5, scale:1.05 }}
                  className="group relative overflow-hidden w-16 h-16 rounded-2xl border border-white/10 bg-white/10 backdrop-blur-2xl flex items-center justify-center text-white text-xl cursor-pointer">
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500 bg-gradient-to-br from-[#F29A0C] to-[#FF8400]" />
                  <span className="relative z-20">{item.icon}</span>
                </motion.a>
              ))}
            </div>
          </div>

          {/* Right: Nested 2-column grid to maintain original column widths */}
          <div className="grid sm:grid-cols-2 gap-10">

            {/* Column 1: Links divided into Navigation & Quick Links side-by-side */}
            <div className="grid grid-cols-2 gap-6">
              {/* Navigation */}
              <div>
                <h4 className="text-white text-lg font-semibold font-poppins">Navigation</h4>
                <div className="space-y-5 mt-8">
                  {navigationLinks.map((item, i) => (
                    <Link key={i} to={item.path}>
                      <motion.div whileHover={{ x:5 }}
                        className="group flex items-center justify-between text-white/65 hover:text-white text-[15px] transition-all duration-300 py-1">
                        <span>{item.name}</span>
                        <FaArrowRight size={10} className="text-[#F29A0C] opacity-0 group-hover:opacity-100 transition-all duration-300" />
                      </motion.div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="text-white text-lg font-semibold font-poppins">Quick Links</h4>
                <div className="space-y-5 mt-8">
                  {quickLinks.map((item, i) => (
                    <Link key={i} to={item.path}>
                      <motion.div whileHover={{ x:5 }}
                        className="group flex items-center justify-between text-white/65 hover:text-white text-[15px] transition-all duration-300 py-1">
                        <span>{item.name}</span>
                        <FaArrowRight size={10} className="text-[#F29A0C] opacity-0 group-hover:opacity-100 transition-all duration-300" />
                      </motion.div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* Column 2: Connect Info Card (maintaining original width) */}
            <div>
              <h4 className="text-white text-lg font-semibold font-poppins">Connect Info</h4>
              <div className="mt-8 rounded-[30px] border border-white/10 bg-white/5 backdrop-blur-2xl p-7">
                <span className="text-white/50 uppercase tracking-[3px] text-[10px] font-semibold">Connect With Us</span>
                <div className="space-y-5 mt-7">
                  <a href="https://www.instagram.com/divyyog.foundation" target="_blank" rel="noopener noreferrer" className="block hover:opacity-80 transition-opacity">
                    <p className="text-white text-sm font-medium">Instagram</p>
                    <p className="text-white/60 text-sm mt-2">@divyyog.foundation</p>
                  </a>
                  <a href="https://www.youtube.com/@divyogafoundation1169" target="_blank" rel="noopener noreferrer" className="block hover:opacity-80 transition-opacity">
                    <p className="text-white text-sm font-medium">YouTube</p>
                    <p className="text-white/60 text-sm mt-2">@divyogafoundation1169</p>
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* EstaffTech Digital Craftsmanship Showcase */}
        <div className="pt-10 border-t border-white/10 mt-12">
          <motion.a
            href="https://estafftech.com/"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.99 }}
            className="group relative block overflow-hidden rounded-2xl border border-[#F29A0C]/25 hover:border-[#F29A0C]/60 bg-gradient-to-r from-black/60 via-[#F29A0C]/[0.08] to-black/60 hover:bg-gradient-to-r hover:from-black/70 hover:via-[#F29A0C]/[0.14] hover:to-black/70 backdrop-blur-2xl p-5 md:p-6 transition-all duration-300 shadow-[0_10px_35px_rgba(0,0,0,0.35)] hover:shadow-[0_15px_45px_rgba(242,154,12,0.18)]"
          >
            {/* Top gold shimmer accent line */}
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#F29A0C]/60 to-transparent" />

            <div className="flex flex-col md:flex-row items-center justify-between gap-5 text-center md:text-left">
              
              {/* Left Info: Icon & Text */}
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#F29A0C] to-[#E05300] flex items-center justify-center text-white shadow-[0_4px_20px_rgba(242,154,12,0.45)] group-hover:scale-105 transition-transform flex-shrink-0">
                  <HiSparkles size={22} />
                </div>
                <div>
                  <div className="flex items-center justify-center sm:justify-start gap-2">
                    <span className="text-[11px] uppercase tracking-[2.5px] font-semibold text-[#F29A0C]">
                      Digital Craftsmanship & Technology
                    </span>
                  </div>
                  <p className="text-white/80 text-sm md:text-base font-normal mt-0.5">
                    This product built by{" "}
                    <span className="text-white font-bold group-hover:text-[#F29A0C] transition-colors tracking-wide">
                      EstaffTech Private Limited
                    </span>
                  </p>
                </div>
              </div>

              {/* Right CTA */}
              <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#F29A0C]/40 group-hover:border-[#F29A0C] bg-[#F29A0C]/10 group-hover:bg-[#F29A0C] text-[#F29A0C] group-hover:text-black text-xs sm:text-sm font-semibold transition-all duration-300 shadow-sm flex-shrink-0">
                <span>Visit estafftech.com</span>
                <FiArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
              </div>

            </div>
          </motion.a>
        </div>

        {/* Bottom Bar: Copyright & Legal links */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8 mt-6 border-t border-white/5 text-xs text-white/50">
          
          {/* Copyright */}
          <div className="text-center md:text-left">
            <p>© 2026 Divyyog Foundation, Gondia. All Rights Reserved.</p>
          </div>

          {/* Legal links */}
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            {legalLinks.map((item, i) => (
              <Link
                key={i}
                to={item.path}
                className="text-white/45 hover:text-[#F29A0C] transition-colors duration-300"
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Release Version */}
          <div className="text-white/35 font-mono text-[11px] tracking-wider">
            v{packageJson.version}
          </div>

        </div>

      </div>
    </footer>
  );
};

export default Footer;